import FixedPointCameraController from './FixedPointCameraController';
import type {Quaternion, Vec3} from 'virtual-tour-viewer/src/modules/React360Proxy';
import CameraUtils from '../utils/CameraUtils';
import {ThreeJsQuaternion, Vector3} from 'virtual-tour-viewer/src/modules/React360Proxy';

const QUARTER_PI = Math.PI / 4;
const DEFAULT_VELOCITY = QUARTER_PI;

export default class PointsOfInterestCameraController extends FixedPointCameraController {
    _poi: Array<Vec3>;
    _poiIndex: number;
    _poiUnits: Array<Vec3>;
    /**
     * Index to the first poi to look at on #next()
     */
    _head: number;
    _currentCameraQuaternionProvider: () => Quaternion;

    constructor({verticalBumper, currentCameraQuaternionProvider, shouldHoldGaze = true}) {
        super({verticalBumper, shouldHoldGaze, velocity: DEFAULT_VELOCITY});
        this._head = null;
        this._poi = [];
        this._poiUnits = [];
        this._poiIndex = -1;
        this._currentCameraQuaternionProvider = currentCameraQuaternionProvider;
    }

    setPoints(points: Array<Vec3>) {
        this._poi = points.slice().sort((a, b) => {
            let yawA = CameraUtils.getEulerFromVec3(a).y;
            let yawB = CameraUtils.getEulerFromVec3(b).y;
            return yawB - yawA;
        });
        this._poiUnits = this._poi.map((v) => {
            let magnitude = CameraUtils.getVectorMagnitude(v);
            return v.map((p) => p / magnitude);
        });
        this._poiIndex = -1;
    }

    disable() {
        super.disable();
        this._head = null;
        this._poiIndex = -1;
    }

    getPoints() {
        return this._poi;
    }

    _setHead() {
        let currentQuaternion = this._currentCameraQuaternionProvider();
        let currentVector: Vec3 = new Vector3(0, 0, -1)
            .applyQuaternion(
                new ThreeJsQuaternion(
                    currentQuaternion[0],
                    currentQuaternion[1],
                    currentQuaternion[2],
                    currentQuaternion[3]
                )
            )
            .normalize()
            .toArray();

        /**
         * Set head to the index of the poi closest to the current view.
         */
        let closestUnitVector = this._poiUnits.slice().sort((a, b) => {
            let distanceA = CameraUtils.getVectorMagnitude([
                currentVector[0] - a[0],
                currentVector[1] - a[1],
                currentVector[2] - a[2],
            ]);
            let distanceB = CameraUtils.getVectorMagnitude([
                currentVector[0] - b[0],
                currentVector[1] - b[1],
                currentVector[2] - b[2],
            ]);
            return distanceA - distanceB;
        })[0];
        this._head = this._poiUnits.indexOf(closestUnitVector);
    }

    next() {
        this._poiIndex++;
        if (this._head === null) {
            this._setHead();
        }
        let success;
        if (this._poiIndex === this._poi.length) {
            this._poiIndex = -1;
            success = false;
        } else {
            let poiIndex = (this._poiIndex + this._head) % this._poi.length;
            this.setPoint(this._poi[poiIndex]);
            success = true;
        }
        return success;
    }

    previous() {
        if (this._poiIndex === -1) {
            this._poiIndex = this._poi.length;
        }
        if (this._head === null) {
            this._setHead();
            /**
             * When starting in reverse, we want to position the head forward one position in
             * the sorted yaw order so that we navigate to the closest first and then continue
             * in reverse yaw order instead of navigating to the last hotspot from a forward cycling
             * perspective.
             */
            this._head = (this._head + 1) % this._poi.length;
        }
        this._poiIndex--;
        let success;
        if (this._poiIndex === -1) {
            success = false;
        } else {
            let poiIndex = (this._poiIndex + this._head) % this._poi.length;
            this.setPoint(this._poi[poiIndex]);
            success = true;
        }
        return success;
    }
}
